import LaunchIcon from '@mui/icons-material/Launch';
import { Badge, Box, IconButton, Typography } from '@mui/material';
import React, { useMemo, useRef } from 'react';
import CustomTabs, { createTabItem } from '../../../core-components/theme/components/CustomTabs';
import { useCommunicationModal } from '../hooks/useCommunicationModal';
import CommunicationList from './CommunicationList';
import { Communication, CommunicationModalProps } from '../types/types';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { ContentBox, HeaderBox, headerButtonSx, StyledPopover, tabStyles } from '../styles/styles';
import { Neutrals, Primary } from '../../../core-components/theme/color-palette/colors';


const BadgeLabel: React.FC<{ label: string; count?: number; isLoading?: boolean; selected?: boolean }> = ({ label, count, isLoading, selected }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2">{label}</Typography>
        <Badge
            badgeContent={typeof count === 'number' ? count : 0}
            color="primary"
            showZero={isLoading === false}
            sx={{

                '& .MuiBadge-badge': selected
                    ? {
                        fontSize: '0.5rem',
                        fontWeight: 700,
                        height: 22,
                        width: 22,
                        borderRadius: '50%',
                        position: 'static',
                        transform: 'none',
                        backgroundColor: '#fff',
                        color: Primary[700],
                        border: `1px solid ${Primary[700]}`,
                        boxShadow: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0,
                    }
                    : {
                        fontSize: '0.6rem',
                        fontWeight: 700,
                        height: 22,
                        width: 22,
                        borderRadius: '50%',
                        position: 'static',
                        transform: 'none',
                        backgroundColor: '#fff',
                        color: Neutrals[300],
                        border: `1px solid ${Neutrals[300]}`,
                        boxShadow: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0,
                    },
            }}
        >
            <Box />
        </Badge>
    </Box>
);


const CommunicationModal: React.FC<CommunicationModalProps> = ({
    open,
    anchorEl,
    onClose,
}) => {
    const {
        communications,
        tabCounts,
        error,
        isError,
        isFetchingNextPage,
        hasNextPage,
        activeTab,
        isLoading,
        fetchNextPage,
        setActiveTab,
        handleNavigateToCommunications,
        handleCloseModal,
        markCommunication,
        tabs
    } = useCommunicationModal({ initialTab: 'ALL', onClose });
    const contentBoxRef = useRef<HTMLDivElement>(null);

    const [sentryRef] = useInfiniteScroll({
        loading: isLoading || isFetchingNextPage,
        hasNextPage,
        onLoadMore: fetchNextPage,
        rootMargin: '0px 0px 400px 0px'
    });

    const tabItems = useMemo(
        () => (tabs || [])
            .filter(tab => typeof tab === 'string' && tab)
            .map((tab: string) =>
                createTabItem(
                    tab,
                    <BadgeLabel
                        label={tab}
                        count={tabCounts?.[tab.toLowerCase()] ?? 0}
                        isLoading={isLoading}
                        selected={activeTab === tab}
                    />
                )
            ),
        [tabs, tabCounts, isLoading, activeTab]
    );

    return (
        <StyledPopover
            ref={contentBoxRef}
            open={open}
            anchorEl={anchorEl}
            onClose={handleCloseModal}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
            <HeaderBox>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Communications
                </Typography>
                <IconButton
                    onClick={handleNavigateToCommunications}
                    sx={headerButtonSx}
                    size="small"
                    title="Go to Communication Center"
                >
                    <LaunchIcon fontSize="small" />
                </IconButton>
            </HeaderBox>

            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <CustomTabs
                    tabs={tabItems}
                    value={activeTab}
                    onChange={v => setActiveTab(v)}
                    variant="underlined"
                    size="medium"
                    fullWidth
                    showContent={false}
                    tabsSx={tabStyles}
                    centered
                />
            </Box>

            <ContentBox ref={contentBoxRef}>
                <CommunicationList
                    key={activeTab}
                    communicationList={communications as Communication[]}
                    isLoading={isLoading}
                    isError={isError}
                    error={error || undefined}
                    isFetchingNextPage={isFetchingNextPage}
                    hasNextPage={hasNextPage}
                    fetchNextPage={fetchNextPage}
                    rootRef={contentBoxRef}
                    handleCommunicationStatus={markCommunication}
                    onItemNavigate={onClose}
                />
                {hasNextPage && (
                    <Box ref={sentryRef} minHeight={"10px"}></Box>
                )}
                {!hasNextPage && !isLoading && !isFetchingNextPage && !communications &&(
                    <Box sx={{ textAlign: 'center', margin: '40px', color: '#aaa' }}>
                        <span>No More Communications</span>
                    </Box>
                )}
            </ContentBox>
        </StyledPopover>
    );
};

export default CommunicationModal; 